import { useEffect, useState } from "react";
import {
  ApiError,
  listAuditors,
  listStandards,
  resendInvitation,
  type AuditorSummary,
} from "../api/client";
import { InviteAuditor } from "./InviteAuditor";

/**
 * Screen 19 — the auditor register.
 *
 * Six columns, and two of them are the point. The **standards** an auditor claims and
 * whether they accepted **AI accountability** are what govern allocation: a missing
 * competence for the selected sector warns, a level below lead auditor blocks, a refused
 * AI declaration blocks, and an excluded client blocks outright. A register that showed
 * only names and statuses would make every one of those refusals invisible until the
 * moment it happened.
 *
 * "Awaiting your verification" is the row that needs work. Cl. 7 makes the determination
 * the certification body's — the auditor asserts, the body decides — so a record sitting
 * unverified is not a queue Complao can clear.
 */
export function AuditorRegister({ onOpen }: { onOpen: (auditorId: string) => void }) {
  const [auditors, setAuditors] = useState<AuditorSummary[] | null>(null);
  const [standards, setStandards] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const load = () => {
    listAuditors()
      .then(setAuditors)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The register could not be read."),
      );
  };

  useEffect(() => {
    load();
    listStandards().then(setStandards).catch(() => undefined);
  }, []);

  /**
   * Send the invitation again — M9-0.
   *
   * A first-password link lives 72 hours, which makes this ordinary rather than
   * exceptional: an auditor invited on a Friday and away for the week needs another. It
   * re-issues rather than re-sends, so the previous link stops working here.
   */
  const resend = async (auditor: AuditorSummary) => {
    setError(null);
    setSent(null);
    setPending(auditor.id);
    try {
      const issued = await resendInvitation(auditor.id);
      setSent(issued.issued_to);
      setAuditors((current) =>
        (current ?? []).map((row) =>
          row.id === auditor.id ? { ...row, credential_link: issued.credential_link } : row,
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "The invitation could not be re-issued.",
      );
    } finally {
      setPending(null);
    }
  };

  const waiting = (auditors ?? []).filter(
    (auditor) => auditor.status === "awaiting_verification",
  ).length;

  return (
    <>
      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Certification body</div>
          <h1>Auditor register</h1>
          <p className="sub">
            Competence records govern which auditors Complao offers at intake
          </p>
        </div>
        <button type="button" className="btn" onClick={() => setInviting(true)}>
          Invite auditor
        </button>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {sent ? (
        <p className="callout green" role="status">
          An invitation was sent to <b>{sent}</b>. The previous link no longer works. If it
          does not arrive, the address may be wrong &mdash; nothing on the platform can tell
          a mistyped address from a correct one, so the auditor is the only person who can.
        </p>
      ) : null}

      <div className="callout">
        <b>
          {waiting === 1
            ? "1 competence record awaiting your verification."
            : `${waiting} competence records awaiting your verification.`}
        </b>{" "}
        The auditor asserts, the certification body verifies. Cl.&nbsp;7 makes that
        determination yours.
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Auditor</th>
                <th>Standards</th>
                <th>Sectors</th>
                <th>Certification</th>
                <th>AI accountability</th>
                <th>Invitation</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {auditors === null ? (
                <tr>
                  <td colSpan={7}>Reading the register&hellip;</td>
                </tr>
              ) : auditors.length === 0 ? (
                <tr>
                  <td colSpan={7}>No auditor has been invited yet.</td>
                </tr>
              ) : (
                auditors.map((auditor) => (
                  <tr key={auditor.id} className="click" onClick={() => onOpen(auditor.id)}>
                    <td>
                      <b>{auditor.name}</b>
                      <br />
                      <span className="sub">{auditor.position ?? "—"}</span>
                    </td>
                    <td>
                      {(auditor.standards ?? []).length === 0
                        ? "—"
                        : (auditor.standards ?? []).map((standard) => (
                            <span
                              key={standard}
                              className="pill p-mute"
                              style={{ margin: "1px 2px 1px 0" }}
                            >
                              {standard}
                            </span>
                          ))}
                    </td>
                    <td>{auditor.sector_count ?? 0} sector(s)</td>
                    <td className="sub">
                      {auditor.certification ?? "—"}
                      {auditor.certification_expiry ? (
                        <>
                          <br />
                          <span className="mono" style={{ fontSize: "10px" }}>
                            exp. {auditor.certification_expiry}
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <AiAccountability accepted={auditor.ai_accountability ?? null} />
                    </td>
                    <td>
                      <Invitation
                        auditor={auditor}
                        busy={pending === auditor.id}
                        onResend={() => void resend(auditor)}
                      />
                    </td>
                    <td>
                      <Standing status={auditor.status ?? null} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {inviting ? (
        <InviteAuditor
          standards={standards}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            load();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Where the invitation went and when, and the button that sends another &mdash; M9-0.
 *
 * *Resend* on its own does not say what will happen, so the cell says when the last one
 * went and to what address first. The link itself is never shown: it exists in one inbox,
 * which is the whole of what makes holding it a claim to that address.
 *
 * **Spent once the auditor sets a password of their own**, and the button goes with it.
 * They have an account they control at that point and *Forgot your password?* is theirs to
 * use; a register that could mint credential links against an auditor's account at will
 * would be a register that could reach the account signing its own findings.
 */
function Invitation({
  auditor,
  busy,
  onResend,
}: {
  auditor: AuditorSummary;
  busy: boolean;
  onResend: () => void;
}) {
  if (auditor.password_set) return <span className="pill p-ok">Accepted</span>;
  return (
    <>
      <div className="sub">
        {auditor.credential_link
          ? `Sent ${formatDate(auditor.credential_link.issued_at)} · expires ${formatDate(
              auditor.credential_link.expires_at,
            )}`
          : "No link sent"}
      </div>
      <button
        type="button"
        className="btn sec sm"
        disabled={busy}
        onClick={(event) => {
          // The row opens the competence record. Without this the resend opens it too.
          event.stopPropagation();
          onResend();
        }}
      >
        Resend
      </button>
    </>
  );
}

function formatDate(value: string): string {
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * Red, and deliberately so.
 *
 * This is one of the two things that stop an allocation outright, and the auditor is
 * allocatable in every other respect — which is exactly the case where a muted badge would
 * be read as "fine". Not accepted is not the same as not yet answered.
 */
function AiAccountability({ accepted }: { accepted: boolean | null }) {
  if (accepted === null) return <span className="pill p-mute">Not yet asserted</span>;
  return accepted ? (
    <span className="pill p-ok">Accepted</span>
  ) : (
    <span className="pill p-red">Not accepted</span>
  );
}

/** The five `auditor_status` values, said in the words screen 19 uses. */
function Standing({ status }: { status: string | null }) {
  switch (status) {
    case "active":
      return <span className="pill p-ok">Active</span>;
    case "awaiting_verification":
      return <span className="pill p-wait">Awaiting your verification</span>;
    case "competence_expiring":
      return <span className="pill p-wait">Competence expiring</span>;
    case "suspended":
      return <span className="pill p-off">Suspended</span>;
    default:
      return <span className="pill p-mute">Invited</span>;
  }
}
